import { useState, useEffect, useCallback, useMemo } from 'react'
import { useCanAccess } from '../../hooks/useCanAccess'
import { useSelectionStore } from '../../stores/selectionStore'
import { useBuildingStore } from '../../stores/buildingStore'
import { pathApi } from '../../services/pathApi'
import { PathfindingEngine } from '../../engine/PathfindingEngine'
import type { PathSegment } from '../../types'
import '../../styles/editors.css'

const SNAP_THRESHOLD = 0.5

const SEGMENT_TYPES = [
  { value: 'corridor', label: 'Corridor' },
  { value: 'escalier', label: 'Escalier' },
  { value: 'ascenseur', label: 'Ascenseur' },
] as const

interface PlacementState {
  active: boolean
  startX: number | null
  startZ: number | null
}

interface GraphStats {
  nodeCount: number
  edgeCount: number
  connectedComponents: number
}

function computeGraphStats(segments: PathSegment[]): GraphStats {
  const engine = new PathfindingEngine()
  engine.buildGraph(segments)

  const nodeCount = engine.nodes.size
  let edgeCount = 0
  for (const node of engine.nodes.values()) {
    edgeCount += node.edges.length
  }

  // Connected components via BFS
  const visited = new Set<string>()
  let connectedComponents = 0
  for (const nodeId of engine.nodes.keys()) {
    if (visited.has(nodeId)) continue
    connectedComponents++
    const queue = [nodeId]
    while (queue.length > 0) {
      const current = queue.shift()!
      if (visited.has(current)) continue
      visited.add(current)
      const node = engine.nodes.get(current)
      if (node) {
        for (const edge of node.edges) {
          if (!visited.has(edge.nodeId)) {
            queue.push(edge.nodeId)
          }
        }
      }
    }
  }

  return { nodeCount, edgeCount, connectedComponents }
}

function snapToExistingEndpoint(
  x: number,
  z: number,
  segments: PathSegment[],
  threshold: number
): { x: number; z: number } {
  let bestDist = threshold
  let snapped = { x, z }

  for (const seg of segments) {
    const dStart = Math.sqrt((seg.start_x - x) ** 2 + (seg.start_z - z) ** 2)
    if (dStart < bestDist) {
      bestDist = dStart
      snapped = { x: seg.start_x, z: seg.start_z }
    }
    const dEnd = Math.sqrt((seg.end_x - x) ** 2 + (seg.end_z - z) ** 2)
    if (dEnd < bestDist) {
      bestDist = dEnd
      snapped = { x: seg.end_x, z: seg.end_z }
    }
  }

  return snapped
}

export default function PathEditor() {
  const currentSiteId = useBuildingStore((s) => s.currentSiteId)
  const pathSegments = useBuildingStore((s) => s.pathSegments)
  const floors = useBuildingStore((s) => s.floors)
  const loadSite = useBuildingStore((s) => s.loadSite)
  const editMode = useSelectionStore((s) => s.editMode)
  const setEditMode = useSelectionStore((s) => s.setEditMode)

  const canCreate = useCanAccess('paths', 'create')
  const canUpdate = useCanAccess('paths', 'update')
  const canDelete = useCanAccess('paths', 'delete')

  const [selectedSegment, setSelectedSegment] = useState<PathSegment | null>(null)
  const [form, setForm] = useState<Partial<PathSegment>>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [placement, setPlacement] = useState<PlacementState>({
    active: false,
    startX: null,
    startZ: null,
  })
  const [placementFloor, setPlacementFloor] = useState(0)

  const isPathMode = editMode === 'path'

  useEffect(() => {
    if (selectedSegment) {
      setForm({ ...selectedSegment })
    } else {
      setForm({})
    }
  }, [selectedSegment])

  const updateField = <K extends keyof PathSegment>(key: K, value: PathSegment[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const stats = useMemo(() => computeGraphStats(pathSegments), [pathSegments])

  const handlePlaneClick = useCallback(
    (x: number, z: number, floor?: number) => {
      if (!placement.active || !currentSiteId || !canCreate) return

      const snapped = snapToExistingEndpoint(x, z, pathSegments, SNAP_THRESHOLD)

      if (floor !== undefined) {
        setPlacementFloor(floor)
      }

      if (placement.startX === null || placement.startZ === null) {
        setPlacement({ active: true, startX: snapped.x, startZ: snapped.z })
      } else {
        const effectiveFloor = floor ?? placementFloor
        const createSegment = async () => {
          setSaving(true)
          setError(null)
          try {
            await pathApi.createSegment(currentSiteId, {
              floor: effectiveFloor,
              start_x: placement.startX!,
              start_z: placement.startZ!,
              end_x: snapped.x,
              end_z: snapped.z,
              width: 1.5,
              bidirectional: true,
              type: 'corridor',
              start_is_transition: false,
              end_is_transition: false,
              connected_floors: [],
            })
            await loadSite(currentSiteId)
            setPlacement({ active: true, startX: null, startZ: null })
          } catch (e) {
            setError(e instanceof Error ? e.message : 'Erreur creation segment')
          }
          setSaving(false)
        }
        createSegment()
      }
    },
    [placement, currentSiteId, canCreate, pathSegments, loadSite, placementFloor],
  )

  // Register plane click handler when placement mode is active
  useEffect(() => {
    if (placement.active) {
      const handler = (event: CustomEvent<{ x: number; z: number; floor: number }>) => {
        handlePlaneClick(event.detail.x, event.detail.z, event.detail.floor)
      }
      window.addEventListener('path-plane-click', handler as EventListener)
      return () => window.removeEventListener('path-plane-click', handler as EventListener)
    }
  }, [placement.active, handlePlaneClick])

  const handleSave = async () => {
    if (!currentSiteId || !selectedSegment) return
    setSaving(true)
    setError(null)
    try {
      await pathApi.updateSegment(currentSiteId, selectedSegment.id, form)
      await loadSite(currentSiteId)
      setSelectedSegment(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur sauvegarde')
    }
    setSaving(false)
  }

  const handleDelete = async () => {
    if (!currentSiteId || !selectedSegment) return
    setSaving(true)
    setError(null)
    try {
      await pathApi.deleteSegment(currentSiteId, selectedSegment.id)
      setSelectedSegment(null)
      await loadSite(currentSiteId)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur suppression')
    }
    setSaving(false)
  }

  const handleDuplicate = async () => {
    if (!currentSiteId || !selectedSegment) return
    setSaving(true)
    setError(null)
    try {
      const dup = await pathApi.createSegment(currentSiteId, {
        floor: selectedSegment.floor,
        start_x: selectedSegment.start_x + 1,
        start_z: selectedSegment.start_z,
        end_x: selectedSegment.end_x + 1,
        end_z: selectedSegment.end_z,
        width: selectedSegment.width,
        bidirectional: selectedSegment.bidirectional,
        cost: selectedSegment.cost,
        type: selectedSegment.type,
        start_is_transition: selectedSegment.start_is_transition,
        end_is_transition: selectedSegment.end_is_transition,
        connected_floors: selectedSegment.connected_floors,
      })
      await loadSite(currentSiteId)
      setSelectedSegment(dup)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur duplication')
    }
    setSaving(false)
  }

  const togglePlacement = () => {
    if (placement.active) {
      setPlacement({ active: false, startX: null, startZ: null })
    } else {
      setPlacement({ active: true, startX: null, startZ: null })
      setSelectedSegment(null)
    }
  }

  if (!canCreate && !canUpdate) return null

  return (
    <div className="editor-panel">
      <div className="panel-section__title">Editeur de chemins</div>

      <div className="editor-panel__toolbar">
        <button
          className={`editor-panel__mode-btn ${isPathMode ? 'editor-panel__mode-btn--active' : ''}`}
          onClick={() => setEditMode(isPathMode ? 'none' : 'path')}
        >
          {isPathMode ? 'Quitter edition' : 'Mode edition'}
        </button>
        {isPathMode && canCreate && (
          <button
            className={`editor-panel__create-btn ${placement.active ? 'editor-panel__mode-btn--active' : ''}`}
            onClick={togglePlacement}
            disabled={saving}
          >
            {placement.active
              ? placement.startX !== null
                ? 'Cliquez fin...'
                : 'Cliquez debut...'
              : '+ Segment'}
          </button>
        )}
      </div>

      {/* Graph stats */}
      <div className="editor-panel__form" style={{ marginBottom: 8 }}>
        <div className="editor-panel__row">
          <div className="editor-panel__field editor-panel__field--half">
            <label>Noeuds</label>
            <span style={{ fontSize: 13, color: '#e0e0e0' }}>{stats.nodeCount}</span>
          </div>
          <div className="editor-panel__field editor-panel__field--half">
            <label>Aretes</label>
            <span style={{ fontSize: 13, color: '#e0e0e0' }}>{stats.edgeCount}</span>
          </div>
          <div className="editor-panel__field editor-panel__field--half">
            <label>Composantes</label>
            <span style={{ fontSize: 13, color: '#e0e0e0' }}>{stats.connectedComponents}</span>
          </div>
        </div>
      </div>

      {/* Segment list */}
      {isPathMode && !selectedSegment && pathSegments.length > 0 && (
        <div style={{ maxHeight: 200, overflowY: 'auto' }}>
          {pathSegments.map((seg) => (
            <button
              key={seg.id}
              className="editor-panel__mode-btn"
              style={{ display: 'block', width: '100%', textAlign: 'left', marginBottom: 2, fontSize: 11 }}
              onClick={() => {
                setSelectedSegment(seg)
                setPlacement({ active: false, startX: null, startZ: null })
              }}
            >
              [{seg.type ?? 'corridor'}] ({seg.start_x.toFixed(1)},{seg.start_z.toFixed(1)}) - ({seg.end_x.toFixed(1)},{seg.end_z.toFixed(1)})
            </button>
          ))}
        </div>
      )}

      {/* Edit form */}
      {selectedSegment && (
        <div className="editor-panel__form">
          <div className="editor-panel__row">
            <div className="editor-panel__field editor-panel__field--half">
              <label>Debut X</label>
              <input
                type="number"
                step="0.1"
                value={form.start_x ?? 0}
                onChange={(e) => updateField('start_x', parseFloat(e.target.value))}
              />
            </div>
            <div className="editor-panel__field editor-panel__field--half">
              <label>Debut Z</label>
              <input
                type="number"
                step="0.1"
                value={form.start_z ?? 0}
                onChange={(e) => updateField('start_z', parseFloat(e.target.value))}
              />
            </div>
          </div>

          <div className="editor-panel__row">
            <div className="editor-panel__field editor-panel__field--half">
              <label>Fin X</label>
              <input
                type="number"
                step="0.1"
                value={form.end_x ?? 0}
                onChange={(e) => updateField('end_x', parseFloat(e.target.value))}
              />
            </div>
            <div className="editor-panel__field editor-panel__field--half">
              <label>Fin Z</label>
              <input
                type="number"
                step="0.1"
                value={form.end_z ?? 0}
                onChange={(e) => updateField('end_z', parseFloat(e.target.value))}
              />
            </div>
          </div>

          <div className="editor-panel__row">
            <div className="editor-panel__field editor-panel__field--half">
              <label>Largeur</label>
              <input
                type="number"
                step="0.1"
                min="0.1"
                value={form.width ?? 1.5}
                onChange={(e) => updateField('width', parseFloat(e.target.value))}
              />
            </div>
            <div className="editor-panel__field editor-panel__field--half">
              <label>Cout</label>
              <input
                type="number"
                step="0.1"
                min="0"
                value={form.cost ?? 0}
                onChange={(e) => {
                  const v = parseFloat(e.target.value)
                  updateField('cost', v > 0 ? v : null)
                }}
              />
            </div>
          </div>

          <div className="editor-panel__field">
            <label>Type</label>
            <select
              value={form.type ?? 'corridor'}
              onChange={(e) => updateField('type', e.target.value)}
            >
              {SEGMENT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          <div className="editor-panel__field">
            <label>Etage</label>
            <select
              value={form.floor ?? 0}
              onChange={(e) => updateField('floor', parseInt(e.target.value))}
            >
              {floors.map((f) => (
                <option key={f.index} value={f.index}>{f.name}</option>
              ))}
            </select>
          </div>

          <div className="editor-panel__field editor-panel__field--checkbox">
            <label>
              <input
                type="checkbox"
                checked={form.bidirectional ?? true}
                onChange={(e) => updateField('bidirectional', e.target.checked)}
              />
              Bidirectionnel
            </label>
          </div>

          {error && <div className="editor-panel__error">{error}</div>}

          <div className="editor-panel__actions">
            {canUpdate && (
              <button
                className="editor-panel__btn editor-panel__btn--save"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Sauvegarde...' : 'Sauvegarder'}
              </button>
            )}
            {canCreate && (
              <button
                className="editor-panel__btn editor-panel__btn--duplicate"
                onClick={handleDuplicate}
                disabled={saving}
              >
                Dupliquer
              </button>
            )}
            {canDelete && (
              <button
                className="editor-panel__btn editor-panel__btn--delete"
                onClick={handleDelete}
                disabled={saving}
              >
                Supprimer
              </button>
            )}
            <button
              className="editor-panel__btn editor-panel__btn--cancel"
              onClick={() => setSelectedSegment(null)}
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {isPathMode && !selectedSegment && pathSegments.length === 0 && (
        <p className="editor-panel__hint">
          Aucun segment. Cliquez &quot;+ Segment&quot; pour placer un chemin.
        </p>
      )}
    </div>
  )
}
